import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, errorMessage } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { ChartPlanPicker } from "@/components/husholdning/ChartPlanPicker";
import { CHART_PLANS } from "@/lib/householdConstants";
import { toast } from "sonner";
import { BookOpenText, Check, Loader2, Briefcase, ShieldAlert } from "lucide-react";

/**
 * Tomtilstand for Regnskap når virksomheten ikke har valgt kontoplan (brukerbeslutning: ingen plan velges automatisk).
 * Eier kan velge direkte her (lagres på virksomheten); fullmektige ser kun en melding.
 * `data` er svaret fra /household/* med `needs_plan: true` (workspace, plan_options, can_choose).
 */
export const ChoosePlanState = ({ data, onChosen }) => {
  const { refreshUser } = useAuth();
  const ws = data?.workspace || {};
  const suggested = (data?.plan_options || []).find((o) => o.suggested && !o.disabled)?.key || null;
  const [plan, setPlan] = useState(suggested);
  const [busy, setBusy] = useState(false);
  const canChoose = data?.can_choose !== false;

  const save = async () => {
    if (!plan) return;
    setBusy(true);
    try {
      await api.put(`/workspaces/${ws.id}`, { chart_plan: plan }, { skipWorkspace: true });
      toast.success(`Kontoplan valgt: ${CHART_PLANS[plan]?.label || plan}`, { description: "Regninger, fakturaer og manuelle poster føres nå etter denne planen." });
      await refreshUser();
      onChosen?.(plan);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card className="max-w-2xl" data-testid="household-choose-plan">
      <CardHeader>
        <div className="flex items-start gap-3">
          <span className="h-11 w-11 rounded-xl bg-accent text-accent-foreground flex items-center justify-center shrink-0"><BookOpenText className="h-5 w-5" /></span>
          <div>
            <CardTitle className="text-lg">Velg kontoplan for {ws.name || "virksomheten"}</CardTitle>
            <CardDescription className="mt-1">
              Regnskapet er ikke tatt i bruk ennå. Kontoplanen bestemmer hvordan regninger, fakturaer og manuelle poster kategoriseres
              {ws.org_number ? " – klienten er registrert i Brønnøysundregistrene og bruker Norsk Standard kontoplan NS 4102." : " – uten org.nr kan klienten føre husholdningsregnskap (COICOP/SIFO) eller bruke NS 4102."}
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-4">
        {canChoose ? (
          <>
            <ChartPlanPicker value={plan} onChange={setPlan} orgNumber={ws.org_number} entityType={ws.entity_type} idPrefix="choose-plan" showLabel={false} testId="household-choose-plan-picker" />
            <div className="flex flex-wrap items-center gap-2">
              <Button onClick={save} disabled={!plan || busy} data-testid="household-choose-plan-submit">{busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />} Ta i bruk kontoplanen</Button>
              <Button variant="ghost" asChild><Link to="/virksomheter" data-testid="household-choose-plan-workspaces-link"><Briefcase className="h-4 w-4 mr-2" /> Åpne Virksomheter</Link></Button>
            </div>
            <p className="text-xs text-muted-foreground">Du kan bytte kontoplan senere under Virksomheter → Rediger. Kategorier og konti lagres per plan, så ingenting går tapt.</p>
          </>
        ) : (
          <Alert data-testid="household-choose-plan-delegate">
            <ShieldAlert className="h-4 w-4" />
            <AlertTitle>Eieren må velge kontoplan</AlertTitle>
            <AlertDescription>Du har fullmakt til denne kontoen, men kontoplanen kan bare velges av eieren under Virksomheter → Rediger. Regnskapet blir tilgjengelig så snart det er gjort.</AlertDescription>
          </Alert>
        )}
      </CardContent>
    </Card>
  );
};
